const SESSION_KEY = "reforge.auth.session.v1";
const API_BASE = "/api/v1/auth";
let csrfTokenCache = null;

export const ROLES = Object.freeze({
  ADMIN: "admin",
  USER: "user",
  MEMBER: "member",
});

const DEFAULT_LOGIN_REDIRECT = "index.html";

function normalizeRole(role) {
  return (role || "").toLowerCase();
}

function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.user?.role) {
      return null;
    }

    const role = normalizeRole(parsed.user.role);
    if (!Object.values(ROLES).includes(role)) {
      return null;
    }

    return {
      ...parsed,
      user: {
        ...parsed.user,
        role,
      },
    };
  } catch (_error) {
    return null;
  }
}

function writeSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function redirectTo(path) {
  if (path) {
    window.location.href = path;
  }
}

async function requestJson(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const skipCsrf = Boolean(options.skipCsrf);

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (!skipCsrf && method !== "GET" && method !== "HEAD") {
    const token = await ensureCsrfToken();
    if (token) {
      headers["X-CSRF-Token"] = token;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...options,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (_error) {
    payload = null;
  }

  if (!response.ok) {
    const message = payload?.error || "Request failed.";
    throw new Error(message);
  }

  return payload;
}

async function ensureCsrfToken() {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  try {
    const payload = await requestJson("/csrf.php", {
      method: "GET",
      skipCsrf: true,
    });
    csrfTokenCache = payload?.csrfToken || null;
    return csrfTokenCache;
  } catch (_error) {
    csrfTokenCache = null;
    return null;
  }
}

function persistSessionFromApi(payload) {
  const session = payload?.session || null;
  if (!session?.user?.role) {
    clearSession();
    return null;
  }

  if (session.csrfToken) {
    csrfTokenCache = session.csrfToken;
  }

  writeSession({
    ...session,
    syncedAt: new Date().toISOString(),
  });
  return readSession();
}

function roleForSession(session) {
  return session?.user?.role || null;
}

function isAdmin(session) {
  return roleForSession(session) === ROLES.ADMIN;
}

function isImpersonating(session) {
  return Boolean(session?.impersonator);
}

function currentUser(session = readSession()) {
  return session?.user || null;
}

async function syncSessionFromServer() {
  try {
    const payload = await requestJson("/me.php", { method: "GET" });
    return persistSessionFromApi(payload);
  } catch (_error) {
    csrfTokenCache = null;
    clearSession();
    return null;
  }
}

function hasAnyRole(roles, session = readSession()) {
  const role = roleForSession(session);
  if (!role) {
    return false;
  }
  return roles.map(normalizeRole).includes(role);
}

function applyFeatureGuards(session) {
  const role = roleForSession(session);

  document.querySelectorAll("[data-member-only]").forEach((element) => {
    element.classList.toggle("hidden", !(role === ROLES.MEMBER || role === ROLES.ADMIN));
  });

  document.querySelectorAll("[data-admin-only]").forEach((element) => {
    element.classList.toggle("hidden", role !== ROLES.ADMIN);
  });

  document.querySelectorAll("[data-user-only]").forEach((element) => {
    element.classList.toggle("hidden", role !== ROLES.USER);
  });
}

function enforceAuthAttributes(session) {
  const body = document.body;
  if (!body) {
    return;
  }

  if (body.hasAttribute("data-auth-required") && !session) {
    redirectTo("signin.html");
    return;
  }

  if (body.hasAttribute("data-auth-guest-only") && session) {
    redirectTo(DEFAULT_LOGIN_REDIRECT);
    return;
  }

  const requiredRoles = body.getAttribute("data-auth-roles");
  if (requiredRoles && session) {
    const allowed = requiredRoles
      .split(",")
      .map((value) => normalizeRole(value.trim()))
      .filter(Boolean);

    if (!allowed.includes(roleForSession(session))) {
      redirectTo(DEFAULT_LOGIN_REDIRECT);
    }
  }
}

function buildImpersonationBanner(session) {
  const existing = document.getElementById("impersonation-banner");
  if (existing) {
    existing.remove();
  }

  if (!isImpersonating(session)) {
    return;
  }

  const display = session.user?.name || session.user?.email || "user";
  const wrapper = document.createElement("div");
  wrapper.id = "impersonation-banner";
  wrapper.className =
    "fixed inset-x-0 top-0 z-[100000] flex items-center justify-center bg-warning-500 px-4 py-2 text-sm font-medium text-black";
  wrapper.innerHTML =
    `<span class="mr-3">Impersonating ${display} (${session.user.role})</span>` +
    '<button type="button" data-stop-impersonation class="rounded bg-black px-2 py-1 text-xs text-white hover:bg-gray-800">Stop</button>';
  document.body.appendChild(wrapper);
}

async function login({ email, password }) {
  const payload = await requestJson("/login.php", {
    method: "POST",
    body: JSON.stringify({
      email: (email || "").trim(),
      password: password || "",
    }),
  });

  const session = persistSessionFromApi(payload);
  if (!session) {
    throw new Error("Login failed.");
  }

  return session;
}

async function signOut() {
  try {
    await requestJson("/logout.php", {
      method: "POST",
      body: JSON.stringify({}),
    });
  } catch (_error) {
    // Clear local state regardless of backend response.
  }

  csrfTokenCache = null;
  clearSession();
  redirectTo("signin.html");
}

async function impersonateAs(targetRole, targetIdentity = {}) {
  const session = readSession();
  if (!session || !isAdmin(session)) {
    return false;
  }

  const role = normalizeRole(targetRole);
  if (!Object.values(ROLES).includes(role) || role === ROLES.ADMIN) {
    return false;
  }

  const payload = await requestJson("/impersonate.php", {
    method: "POST",
    body: JSON.stringify({
      role,
      email: targetIdentity.email || "",
      name: targetIdentity.name || "",
    }),
  });

  const next = persistSessionFromApi(payload);
  if (!next) {
    return false;
  }

  redirectTo(DEFAULT_LOGIN_REDIRECT);
  return true;
}

async function stopImpersonation() {
  const session = readSession();
  if (!session?.impersonator) {
    return false;
  }

  const payload = await requestJson("/stop-impersonation.php", {
    method: "POST",
    body: JSON.stringify({}),
  });

  const next = persistSessionFromApi(payload);
  if (!next) {
    return false;
  }

  redirectTo(DEFAULT_LOGIN_REDIRECT);
  return true;
}

function setupSigninForm() {
  const form = document.getElementById("signin-form");
  if (!form) {
    return;
  }

  const emailInput = document.getElementById("email");
  const passwordInput = document.getElementById("password");
  const errorBox = document.getElementById("signin-error");

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    if (errorBox) {
      errorBox.textContent = "";
      errorBox.classList.add("hidden");
    }

    try {
      await login({
        email: emailInput?.value,
        password: passwordInput?.value,
      });
      redirectTo(DEFAULT_LOGIN_REDIRECT);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message || "Login failed.";
        errorBox.classList.remove("hidden");
      }
    }
  });
}

function setupEventBindings() {
  document.querySelectorAll("[data-signout]").forEach((element) => {
    element.addEventListener("click", async (event) => {
      event.preventDefault();
      await signOut();
    });
  });

  document.querySelectorAll("[data-stop-impersonation]").forEach((element) => {
    element.addEventListener("click", async (event) => {
      event.preventDefault();
      await stopImpersonation();
    });
  });

  document.querySelectorAll("[data-impersonate-role]").forEach((element) => {
    element.addEventListener("click", async (event) => {
      event.preventDefault();
      await impersonateAs(element.getAttribute("data-impersonate-role"), {
        email: element.getAttribute("data-impersonate-email"),
        name: element.getAttribute("data-impersonate-name"),
      });
    });
  });
}

function exposeAuthApi() {
  window.ReforgeAuth = {
    getSession: readSession,
    getCurrentUser: currentUser,
    getCsrfToken: ensureCsrfToken,
    hasAnyRole,
    login,
    signOut,
    impersonateAs,
    stopImpersonation,
  };
}

export function initAuth() {
  setupSigninForm();
  exposeAuthApi();
  const session = readSession();
  applyFeatureGuards(session);
  enforceAuthAttributes(session);
  buildImpersonationBanner(session);
  setupEventBindings();

  // Reconcile local cache with backend session state on every page load.
  syncSessionFromServer().then((freshSession) => {
    applyFeatureGuards(freshSession);
    enforceAuthAttributes(freshSession);
    buildImpersonationBanner(freshSession);
  });

  // Reconcile feature flags (beta toggles) on every page load.
  fetchFeatures().then(applyAppFeatureToggles);
}

function applyAppFeatureToggles(features = {}) {
  document.querySelectorAll("[data-feature]").forEach((element) => {
    const featureName = element.getAttribute("data-feature");
    // Hide if the feature explicitly evaluates to a falsy value in config
    element.classList.toggle("hidden", !features[featureName]);
  });
}

function fetchFeatures() {
  return fetch('/api/v1/features.php', { headers: { 'Accept': 'application/json' } })
    .then((res) => res.json())
    .then((data) => data.features || {})
    .catch(() => ({}));
}

export { signOut };
